export type IdentityErrorCode =
  | "invalid_credentials"
  | "token_expired"
  | "session_invalid"
  | "oauth_client_mismatch"
  | "permission_denied"
  | "tenant_not_found"
  | "sso_callback_mismatch"
  | "unsupported_oauth_provider"
  | "missing_oauth_scope"
  | "verification_required"
  | "verification_expired"
  | "email_already_in_use"
  | "password_too_short"
  | "password_mismatch"
  | "new_device"
  | "suspicious_login"
  | "refresh_token_revoked"
  | "invalid_email"
  | "unknown";

export interface IdentityError {
  code?: IdentityErrorCode;
  message: string;
}

export type IdentityFormMode = "login" | "signup";

export type IdentityButtonVariant = "primary" | "secondary";

export type IdentitySSOStage = "start" | "callback" | "complete";

export type IdentityStackSpace = 8 | 16 | 24;

export interface IdentityFormData {
  email: string;
  password: string;
  confirmPassword?: string;
}

export type PasswordStrength = "weak" | "medium" | "strong" | "very-strong";

export const CANONICAL_ERROR_MESSAGES: Readonly<Record<IdentityErrorCode, string>> = {
  invalid_credentials: "SpeedyCore Identity: Invalid credentials",
  token_expired: "SpeedyCore Identity: Token expired",
  session_invalid: "SpeedyCore Identity: Session invalid",
  oauth_client_mismatch: "SpeedyCore Identity: OAuth client mismatch",
  permission_denied: "SpeedyCore Identity: Permission denied",
  tenant_not_found: "SpeedyCore Identity: Tenant not found",
  sso_callback_mismatch: "SpeedyCore Identity: SSO callback mismatch",
  unsupported_oauth_provider: "SpeedyCore Identity: Unsupported OAuth provider",
  missing_oauth_scope: "SpeedyCore Identity: Missing OAuth scope",
  verification_required: "Please verify your email to continue",
  verification_expired: "Verification link expired — request a new one",
  email_already_in_use: "Email already in use",
  password_too_short: "Password too short",
  password_mismatch: "Passwords do not match",
  new_device: "You're signing in from a new device.",
  suspicious_login: "We detected an unusual login attempt. Please reset your password.",
  refresh_token_revoked: "Your session has expired — please sign in again.",
  invalid_email: "Please enter a valid email address.",
  unknown: "SpeedyCore Identity: Authentication error",
};

export function canonicalErrorMessage(
  code: IdentityErrorCode | undefined | null,
  fallback: string,
): string {
  if (!code) return fallback;
  return CANONICAL_ERROR_MESSAGES[code] ?? fallback;
}

export const CANONICAL_SUCCESS_MESSAGES = {
  signedIn: "You're signed in.",
  accountReady: "Your account is ready.",
  emailVerified: "Email verified.",
  sessionRefreshed: "Session refreshed.",
  passwordChanged: "Password updated.",
  emailChanged: "Email updated.",
  verificationSent: "Verification email sent.",
} as const;

export const CANONICAL_LOADING_MESSAGES = {
  signingIn: "Signing in…",
  creatingAccount: "Creating your account…",
  refreshingSession: "Refreshing your SpeedyCore session…",
  verifyingEmail: "Verifying your email…",
  sendingVerification: "Sending verification email…",
} as const;

export const CANONICAL_SSO_MESSAGES: Readonly<Record<IdentitySSOStage, string>> = {
  start: "Signing in with SpeedyCore Identity…",
  callback: "Completing secure login…",
  complete: "You're signed in across all SpeedyCore sites.",
};

export const CANONICAL_SESSION_MESSAGES = {
  refreshing: "Refreshing your SpeedyCore session…",
  expired: "Your session has expired — please sign in again.",
} as const;

export const CANONICAL_SECURITY_MESSAGES = {
  breachCheck:
    "SpeedyCore Identity checks your password against known breach databases to keep your account safe.",
  newDevice: "You're signing in from a new device.",
  suspiciousLogin:
    "We detected an unusual login attempt. Please reset your password.",
  newLoginEmail: "New login to your SpeedyCore account.",
} as const;

export const PASSWORD_STRENGTH_LABELS: Record<PasswordStrength, string> = {
  weak: "Weak",
  medium: "Medium",
  strong: "Strong",
  "very-strong": "Very strong",
};

export const IDENTITY_BUTTON_LABELS = {
  continueWithSpeedyCore: "Continue with SpeedyCore",
  createSpeedyCoreAccount: "Create SpeedyCore account",
  resendVerification: "Resend verification email",
  approve: "Approve",
  deny: "Deny",
  saveChanges: "Save changes",
  changeEmail: "Change email",
  changePassword: "Change password",
  logoutAllSessions: "Logout all sessions",
  deleteAccount: "Delete account",
  signInAgain: "Sign in again",
  sendResetLink: "Send reset link",
  updatePassword: "Update password",
} as const;

export type ScLegalBannerProps = {
  variant?: "footer" | "inline" | "app";
  className?: string;
};
